import { AppShell } from "@astryxdesign/core/AppShell";
import { Badge } from "@astryxdesign/core/Badge";
import { Icon, type IconType } from "@astryxdesign/core/Icon";
import { MoreMenu } from "@astryxdesign/core/MoreMenu";
import { HStack, VStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { TopNav, TopNavHeading, TopNavItem } from "@astryxdesign/core/TopNav";
import { useToast } from "@astryxdesign/core/Toast";
import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { api } from "../lib/api";
import {
  DownloadIcon,
  HqMark,
  InboxIcon,
  KeyIcon,
  LogoutIcon,
  PowerIcon,
  WalletIcon,
} from "../lib/icons";
import { registerPasskey } from "../lib/passkey";

type Tab = "board" | "money" | "sites";

interface Props {
  active: Tab;
  /** Charges due or overdue — shown as a badge on the Money tab. */
  dueCount?: number;
  children: ReactNode;
}

/**
 * Nav + shell shared by the board, money and sites screens.
 *
 * Responsive contract:
 *   > 768px  TopNav carries the Board/Money/Sites tabs
 *   <= 768px tabs move to a bottom bar (thumb reach); the hamburger drawer stays
 *            disabled — three destinations still fit a bar, and a drawer would
 *            hide the kill switch behind two taps
 */
export function AppFrame({ active, dueCount = 0, children }: Props) {
  const navigate = useNavigate();
  const toast = useToast();

  const goBoard = () => void navigate({ to: "/", search: { c: "all", s: "open" } });
  const goMoney = () => void navigate({ to: "/money", search: {} });
  const goSites = () => void navigate({ to: "/sites" });

  async function logout() {
    await api.logout().catch(() => {});
    await navigate({ to: "/login" });
  }

  const menuItems = [
    {
      label: "Export backup",
      icon: DownloadIcon,
      onClick: () => {
        window.location.href = "/api/export";
      },
    },
    {
      label: "Add passkey to this device",
      icon: KeyIcon,
      onClick: () => {
        void registerPasskey()
          .then(() => toast({ body: "Passkey added ✓", uniqueID: "passkey" }))
          .catch(() =>
            toast({ body: "Passkey setup failed", type: "error", uniqueID: "passkey" }),
          );
      },
    },
    { type: "divider" as const },
    { label: "Log out", icon: LogoutIcon, onClick: () => void logout() },
  ];

  return (
    <AppShell
      contentPadding={0}
      mobileNav={false}
      topNav={
        <TopNav
          heading={<TopNavHeading logo={<HqMark />} heading="hq" />}
          startContent={
            <span className="hq-wide-only">
              <HStack gap={0}>
                <TopNavItem label="Board" isSelected={active === "board"} onClick={goBoard} />
                <TopNavItem
                  label="Money"
                  isSelected={active === "money"}
                  onClick={goMoney}
                  {...(dueCount > 0
                    ? { icon: <Badge variant="error" label={String(dueCount)} /> }
                    : {})}
                />
                <TopNavItem label="Sites" isSelected={active === "sites"} onClick={goSites} />
              </HStack>
            </span>
          }
          endContent={<MoreMenu label="Menu" items={menuItems} />}
        />
      }
    >
      {children}
      <nav className="hq-tabbar" aria-label="Sections">
        <TabButton label="Board" icon={InboxIcon} isActive={active === "board"} onClick={goBoard} />
        <TabButton
          label="Money"
          icon={WalletIcon}
          isActive={active === "money"}
          onClick={goMoney}
          badge={dueCount}
        />
        <TabButton
          label="Sites"
          icon={PowerIcon}
          isActive={active === "sites"}
          onClick={goSites}
        />
      </nav>
    </AppShell>
  );
}

interface TabProps {
  label: string;
  icon: IconType;
  isActive: boolean;
  onClick: () => void;
  badge?: number;
}

function TabButton({ label, icon, isActive, onClick, badge = 0 }: TabProps) {
  return (
    <button
      type="button"
      className="hq-tabbar-item"
      data-active={isActive}
      aria-current={isActive ? "page" : undefined}
      onClick={onClick}
    >
      <VStack gap={0.5} hAlign="center">
        <span style={{ position: "relative", display: "inline-flex" }}>
          <Icon icon={icon} color={isActive ? "accent" : "secondary"} />
          {badge > 0 ? (
            <span className="hq-tabbar-badge">
              <Badge variant="error" label={String(badge)} />
            </span>
          ) : null}
        </span>
        <Text type="supporting" size="xsm" color={isActive ? "accent" : "secondary"}>
          {label}
        </Text>
      </VStack>
    </button>
  );
}
